// packages/shared/src/villagers/pause.ts — the talk pause rule (spec §9.13, rev 5 item 2)
import { tuning } from '@whiskerton/content';
import type { DaySchedule, PlaceId, ScheduleSeg, VillagerPause } from '../types/villagers';
import { DAY_START_MIN, walkPose } from './schedule';

const toMin = (ms: number, dayStartMs: number) => DAY_START_MIN + (ms - dayStartMs) / 60_000;

/** Pause rule (spec §9.13): a walk interrupted by a pause resumes where it stopped; the next idle absorbs the delay,
 *  carrying the rest over when it is shorter. An open pause (end null) counts as tuning.villagers.pauseMaxMinutes long. */
export function applyPauses(s: DaySchedule, pauses: readonly VillagerPause[], dayStartMs: number): DaySchedule {
  const max = tuning.villagers.pauseMaxMinutes;
  const mine = pauses.filter((p) => p.villager === s.villager && p.dayKey === s.dayKey)
    .sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
  let segs = s.segs;
  for (const p of mine) {
    const p0 = toMin(p.start, dayStartMs);
    const p1 = Math.min(p0 + max, p.end === null ? p0 + max : toMin(p.end, dayStartMs));
    if (p1 > p0) segs = applyOne(segs, p0, p1);
  }
  return segs === s.segs ? s : { ...s, segs };
}

function applyOne(segs: readonly ScheduleSeg[], p0: number, p1: number): ScheduleSeg[] {
  const i = segs.findIndex((g) => p0 >= g.from && p0 < g.to);
  if (i < 0) return [...segs];
  const g = segs[i]!;
  if (g.kind === 'home') return [...segs];                       // talking indoors moves nobody
  const out: ScheduleSeg[] = segs.slice(0, i);
  let shift: number;
  if (g.kind === 'walk') {
    const [a, b] = g.span ?? [0, 1];
    const frac = a + ((b - a) * (p0 - g.from)) / (g.to - g.from);
    const stop = walkPose(g, p0);
    // Labelled with the place the walk left (never the destination, so the first idle at a place stays the real stop there).
    const place: PlaceId = segs.slice(0, i).findLast((h): h is Extract<ScheduleSeg, { kind: 'idle' }> => h.kind === 'idle')?.place ?? 'nearHouse';
    if (p0 > g.from) out.push({ ...g, to: p0, span: [a, frac] });
    out.push({ kind: 'idle', from: p0, to: p1, at: { x: Math.floor(stop.pos.x), y: Math.floor(stop.pos.y) }, place, idle: 'stand', pos: stop.pos, f: stop.f });
    out.push({ ...g, from: p1, to: g.to + (p1 - p0), span: [frac, b] });
    shift = p1 - p0;
  } else {
    if (p0 > g.from) out.push({ ...g, to: p0 });
    out.push({ ...g, from: p0, to: p1, idle: 'stand' });
    if (p1 < g.to) { out.push({ ...g, from: p1 }); return [...out, ...segs.slice(i + 1)]; }
    shift = p1 - g.to;
  }
  for (const h of segs.slice(i + 1)) {
    if (shift <= 0) { out.push(h); continue; }
    if (h.kind === 'idle') {
      const from = h.from + shift;
      if (from < h.to) { out.push({ ...h, from }); shift = 0; } else shift = from - h.to;   // fully absorbed → carry the rest
    } else if (h.kind === 'home') {
      out.push({ ...h, from: Math.min(h.from + shift, h.to) });
    } else {
      out.push({ ...h, from: h.from + shift, to: h.to + shift });
    }
  }
  return out;
}
